import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, MailWarning } from "lucide-react";
import { completeCheckout, establishSessionFromCheckout, type CompleteCheckoutResult } from "@/lib/completeCheckout";
import { claimEntitlements } from "@/lib/claimEntitlements";
import { PaymentShell, BRAND_GRADIENT } from "@/components/payment/PaymentShell";
import { useAuth } from "@/contexts/AuthContext";

type Phase =
  | "working"
  | "success"
  | "unpaid"
  | "mismatch"
  | "missing"
  | "error";

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function claimWithRetry(opts: { stripeCustomerId?: string; domain?: string }) {
  for (let i = 0; i < 3; i++) {
    const res = await claimEntitlements(opts);
    if (res.success) return res;
    await sleep(1500);
  }
  return { success: false as const };
}

/** Survives React Strict Mode remount so we don't mint two OTPs. */
const inflight = new Map<string, Promise<CompleteCheckoutResult>>();

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const sessionId = (params.get("session_id") || "").trim();
  const [phase, setPhase] = useState<Phase>(sessionId ? "working" : "missing");
  const [detail, setDetail] = useState<CompleteCheckoutResult | null>(null);
  const [message, setMessage] = useState("Confirming your payment…");

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const run = async () => {
      try {
        let pending = inflight.get(sessionId);
        if (!pending) {
          pending = completeCheckout(sessionId);
          inflight.set(sessionId, pending);
        }
        const result = await pending;
        if (cancelled) return;

        if (result.reason === "email_mismatch") {
          setDetail(result);
          setPhase("mismatch");
          return;
        }

        if (result.paid !== true) {
          setDetail(result);
          setPhase("unpaid");
          return;
        }

        const alreadyRightUser =
          !!user?.email &&
          !!result.email &&
          user.email.trim().toLowerCase() === result.email.trim().toLowerCase();

        if (!alreadyRightUser) {
          setMessage("Setting up your account…");
          const established = await establishSessionFromCheckout(result);
          if (cancelled) return;
          if (!established.ok) {
            setDetail({ ...result, error: established.error });
            setPhase("error");
            return;
          }
        }

        setMessage("Linking your plan and audit data…");
        await claimWithRetry({
          stripeCustomerId: result.stripe_customer_id || undefined,
          domain: result.domain || undefined,
        });
        if (cancelled) return;

        window.history.replaceState({}, "", "/payment/success");
        setDetail(result);
        setPhase("success");
        setTimeout(() => navigate("/dashboard", { replace: true }), 1400);
      } catch (err) {
        if (cancelled) return;
        setDetail({
          paid: false,
          reason: "error",
          error: err instanceof Error ? err.message : "Unexpected error",
        });
        setPhase("error");
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [sessionId, user?.email, navigate]);

  useEffect(() => {
    if (phase === "missing" && !authLoading && user) {
      navigate("/dashboard", { replace: true });
    }
  }, [phase, authLoading, user, navigate]);

  if (phase === "working") {
    return (
      <PaymentShell
        eyebrow="Payment"
        title="Almost there"
        icon={<Loader2 className="h-6 w-6 animate-spin text-og01-violet" />}
      >
        <p className="text-[15px] text-og01-text-secondary">{message}</p>
        <p className="mt-2 text-[13px] text-og01-text-muted">This only takes a moment. Don’t close this tab.</p>
      </PaymentShell>
    );
  }

  if (phase === "success") {
    const plan = detail?.product?.name || detail?.product?.tier || "your plan";
    return (
      <PaymentShell
        eyebrow="Payment"
        title="You're in"
        icon={<CheckCircle2 className="h-6 w-6" style={{ color: "#22C55E" }} />}
      >
        <p className="text-[15px] text-og01-text-secondary">
          {detail?.created_user ? "Account created" : "Welcome back"}
          {detail?.email ? ` for ${detail.email}` : ""}. {plan} is active
          {detail?.domain ? ` for ${detail.domain}` : ""}.
        </p>
        <p className="mt-3 text-[13px] text-og01-text-muted">Taking you to your dashboard…</p>
      </PaymentShell>
    );
  }

  if (phase === "mismatch") {
    return (
      <PaymentShell
        eyebrow="Payment"
        title="Wrong account"
        icon={<MailWarning className="h-6 w-6 text-og01-violet" />}
        footer={
          <Link to="/auth/login" className="text-[13px] text-og01-text-secondary underline-offset-2 hover:underline">
            Sign in with the payment email
          </Link>
        }
      >
        <p className="text-[15px] text-og01-text-secondary">
          This payment belongs to <span className="text-og01-text">{detail?.email}</span>
          {detail?.logged_in_email ? `, but you're signed in as ${detail.logged_in_email}` : ""}.
        </p>
        <p className="mt-3 text-[13px] text-og01-text-muted">
          Sign out, then sign in with the email you used at checkout. We won’t attach this purchase to the wrong user.
        </p>
      </PaymentShell>
    );
  }

  if (phase === "missing") {
    return (
      <PaymentShell
        eyebrow="Payment"
        title="No session found"
        icon={<XCircle className="h-6 w-6" style={{ color: "#EF4444" }} />}
        footer={<RetryLinks />}
      >
        <p className="text-[15px] text-og01-text-secondary">
          This page needs a Stripe checkout session. If you just paid, return from Stripe using the link in your receipt, or sign in — your plan is attached by email once the webhook lands.
        </p>
      </PaymentShell>
    );
  }

  const unpaidCopy =
    detail?.reason === "expired"
      ? "This checkout session expired before payment completed."
      : detail?.reason === "canceled" || detail?.reason === "unpaid"
        ? "Payment was not completed."
        : "We couldn’t confirm this payment with Stripe.";

  return (
    <PaymentShell
      eyebrow="Payment"
      title={phase === "unpaid" ? "Payment not confirmed" : "Something went wrong"}
      icon={<XCircle className="h-6 w-6" style={{ color: "#EF4444" }} />}
      footer={<RetryLinks />}
    >
      <p className="text-[15px] text-og01-text-secondary">{unpaidCopy}</p>
      {detail?.error ? (
        <p className="mt-2 text-[12.5px] text-og01-text-muted">{detail.error}</p>
      ) : null}
      <p className="mt-3 text-[13px] text-og01-text-muted">
        No account was created and no plan was granted. You can retry checkout or sign in if you already have an account.
      </p>
    </PaymentShell>
  );
};

const RetryLinks = () => (
  <div className="flex items-center justify-center gap-4 text-[13px]">
    <a
      href="https://og01.ai/remediation-pricing"
      className="font-semibold text-white underline-offset-2 hover:underline"
      style={{ background: BRAND_GRADIENT, WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent" }}
    >
      Back to plans
    </a>
    <span className="text-og01-text-muted">·</span>
    <Link to="/auth/login" className="text-og01-text-secondary underline-offset-2 hover:underline">
      Sign in
    </Link>
  </div>
);

export default PaymentSuccess;
